import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
} from "@mui/material";
import {
  Clear,
  Close,
  DownloadOutlined,
  ExpandLess,
  ExpandMore,
  FilterList,
  ReplayOutlined,
  Search,
} from "@mui/icons-material";
import { observer } from "mobx-react";
import { useNotification } from "../../../../../utils/NotificationsContext";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { SwayyPageHeader, SwayyTableEmptyState } from "../../components/SwayyPageShell";
import { swayyInvoiceStore } from "../../../stores/SwayyInvoiceStore";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { userStore } from "../../../../../stores/sharedStores";
import { SwayyEmployeeRoles } from "../../../../../models/Employees/SwayyEmployeeRoles";
import {
  SWAYY_INVOICE_STATUS_OPTIONS,
  getSwayyInvoiceStatusLabel,
  getSwayyInvoiceStatusTone,
} from "../../../../../models/SwayyInvoice/SwayyInvoiceFilters";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { CurrencyHelper } from "../../../../../utils/CurrencyHelper";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";

const thSx = {
  fontSize: "0.7rem",
  fontWeight: 600,
  color: swayyColors.textSecondary,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  whiteSpace: "nowrap" as const,
  py: 1.5,
  px: 2,
  borderBottom: `1px solid ${swayyColors.border}`,
  backgroundColor: swayyColors.pageBg,
  fontFamily: swayyFont,
  textAlign: "center" as const,
};

const thLeftSx = {
  ...thSx,
  textAlign: "left" as const,
};

const tdSx = {
  fontSize: "0.82rem",
  fontWeight: 400,
  color: swayyColors.textPrimary,
  py: 1.5,
  px: 2,
  borderBottom: `1px solid ${swayyColors.border}`,
  fontFamily: swayyFont,
  textAlign: "center" as const,
};

const tdLeftSx = {
  ...tdSx,
  textAlign: "left" as const,
};

const stickyActionsThSx = {
  ...thSx,
  position: "sticky" as const,
  right: 0,
  zIndex: 3,
  minWidth: 96,
  width: 96,
  boxShadow: "-6px 0 12px rgba(0, 81, 84, 0.06)",
};

const stickyActionsTdSx = {
  ...tdSx,
  position: "sticky" as const,
  right: 0,
  zIndex: 2,
  minWidth: 96,
  width: 96,
  bgcolor: swayyColors.white,
  boxShadow: "-6px 0 12px rgba(0, 81, 84, 0.06)",
};

const actionBtnSx = {
  color: swayyColors.deepGreen,
  border: `1px solid ${swayyColors.border}`,
  borderRadius: swayyRadii.sm,
  width: 32,
  height: 32,
  "&:hover": { bgcolor: swayyColors.pineLight, borderColor: swayyColors.pine },
};

const TYPE_OPTIONS: FilterValue[] = [
  { id: "invoice", value: "Invoice" },
  { id: "credit_note", value: "Credit note" },
];

const PAGE_SIZE_OPTIONS: FilterValue[] = [
  { id: "10", value: "10 / page" },
  { id: "20", value: "20 / page" },
  { id: "50", value: "50 / page" },
  { id: "100", value: "100 / page" },
];

const SEARCH_DEBOUNCE_MS = 400;

const FILTER_FIELD_HEIGHT = 42;

const filterItemSx = {
  flex: "1 1 180px",
  minWidth: { xs: "100%", sm: 160 },
  maxWidth: "100%",
};

const filterSearchItemSx = {
  flex: "1 1 260px",
  minWidth: { xs: "100%", sm: 220 },
  maxWidth: "100%",
};

const filterDropdownWrapSx = {
  width: "100%",
  "& > div > div:first-of-type": {
    height: `${FILTER_FIELD_HEIGHT}px !important`,
    minHeight: `${FILTER_FIELD_HEIGHT}px !important`,
    maxHeight: `${FILTER_FIELD_HEIGHT}px !important`,
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    boxSizing: "border-box",
    borderRadius: `${swayyRadii.sm} !important`,
    borderColor: `${swayyColors.border} !important`,
    "&:hover": {
      borderColor: `${swayyColors.pine} !important`,
    },
  },
};

const searchInputSx = {
  borderRadius: swayyRadii.sm,
  fontFamily: swayyFont,
  fontSize: 13,
  height: FILTER_FIELD_HEIGHT,
  width: "100%",
  bgcolor: swayyColors.white,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.border,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
};

const StatusBadge = ({ status }: { status: string }) => {
  const tone = getSwayyInvoiceStatusTone(status);
  const styles: Record<string, { bg: string; color: string }> = {
    pine: { bg: swayyColors.pineLight, color: swayyColors.deepGreen },
    success: { bg: "rgba(30, 166, 114, 0.12)", color: swayyColors.success },
    warning: { bg: "rgba(217, 119, 6, 0.12)", color: swayyColors.warning },
    danger: { bg: "rgba(220, 38, 38, 0.1)", color: swayyColors.danger },
    muted: { bg: swayyColors.pageBg, color: swayyColors.textSecondary },
  };
  const toneStyle = styles[tone];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1.25,
        py: 0.35,
        borderRadius: swayyRadii.full,
        bgcolor: toneStyle.bg,
        color: toneStyle.color,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: swayyFont,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {getSwayyInvoiceStatusLabel(status)}
    </Box>
  );
};

const SwayyInvoices = () => {
  const store = swayyInvoiceStore;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError, showSuccess } = useNotification();
  const canEditInvoices = userStore.hasSwayyPermission(
    SwayyEmployeeRoles.EDIT_INVOICES,
  );
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [urlReady, setUrlReady] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipFirstSearchEffect = useRef(true);

  const handleLoadInvoices = async () => {
    try {
      await store.getInvoices();
    } catch {
      showError("Error", "Failed to load invoices.");
    }
  };

  const handleClearSearch = () => {
    store.setSearchDraft("");
    store.applySearch();
  };

  const handleClearFilters = () => {
    store.clearFilters();
    setMobileFiltersOpen(false);
  };

  const handleApplyFilters = () => {
    store.applyFilters();
    setMobileFiltersOpen(false);
  };

  const handleOpenMobileFilters = () => {
    setMobileFiltersOpen(true);
  };

  const handleCloseMobileFilters = () => {
    setMobileFiltersOpen(false);
  };

  const handleOpenOrder = (orderId: number) => {
    navigate(`/orders/${orderId}`);
  };

  const handleDownloadInvoice = (url: string) => {
    window.open(url, "_blank");
  };

  const handleRegenerateInvoice = async (invoiceId: number) => {
    try {
      await store.regenerateInvoice(invoiceId);
      showSuccess("Invoice regeneration queued.");
    } catch (error: any) {
      showError("Error", error?.message || "Failed to regenerate.");
    }
  };

  const handleRowsPerPageChange = (items: FilterValue[]) => {
    store.handleRowsPerPageChange(Number(items[0]?.id || 20));
  };

  const handlePageChange = (_: unknown, page: number) => {
    store.handlePageChange(page);
  };

  useEffect(() => {
    if (
      !userStore.hasSwayyPermission([
        SwayyEmployeeRoles.VIEW_INVOICES,
        SwayyEmployeeRoles.EDIT_INVOICES,
      ])
    ) {
      return;
    }

    store.initializeParamsFromSearch(searchParams);
    setUrlReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !urlReady ||
      !userStore.hasSwayyPermission([
        SwayyEmployeeRoles.VIEW_INVOICES,
        SwayyEmployeeRoles.EDIT_INVOICES,
      ])
    ) {
      return;
    }
    const next = store.buildSearchParams();
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    urlReady,
    store.params.page,
    store.params.limit,
    store.params.q,
    store.params.status,
    store.params.type,
  ]);

  useEffect(() => {
    if (
      !urlReady ||
      !userStore.hasSwayyPermission([
        SwayyEmployeeRoles.VIEW_INVOICES,
        SwayyEmployeeRoles.EDIT_INVOICES,
      ])
    ) {
      return;
    }
    void handleLoadInvoices();
  }, [
    urlReady,
    store.params.page,
    store.params.limit,
    store.params.q,
    store.params.status,
    store.params.type,
  ]);

  useEffect(() => {
    if (
      !urlReady ||
      !userStore.hasSwayyPermission([
        SwayyEmployeeRoles.VIEW_INVOICES,
        SwayyEmployeeRoles.EDIT_INVOICES,
      ])
    ) {
      return;
    }
    if (skipFirstSearchEffect.current) {
      skipFirstSearchEffect.current = false;
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      store.applySearch();
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [store.searchDraft, urlReady]);

  const total = store.pagination.total || 0;
  const page = store.params.page;
  const limit = store.params.limit || 20;
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const pageCount = Math.max(1, Math.ceil(total / limit) || 1);

  const paginationFooter = (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${swayyColors.border}`,
      }}
    >
      <CustomTypography
        variant="body3"
        color={swayyColors.textSecondary}
        sx={{ fontFamily: swayyFont, fontSize: 12 }}
      >
        Showing {from}–{to} of {total} invoices
      </CustomTypography>

      <Pagination
        count={pageCount}
        page={page}
        onChange={handlePageChange}
        shape="rounded"
        size="small"
        sx={{
          "& .MuiPaginationItem-root": {
            fontFamily: swayyFont,
            color: swayyColors.deepGreen,
            borderRadius: swayyRadii.sm,
          },
          "& .Mui-selected": {
            bgcolor: `${swayyColors.pine} !important`,
            color: `${swayyColors.white} !important`,
            "&:hover": { bgcolor: `${swayyColors.pineHover} !important` },
          },
        }}
      />

      <Box sx={{ minWidth: 120 }}>
        <AppSearchDropdownMulti
          title="Rows"
          isMultiSelect={false}
          list={PAGE_SIZE_OPTIONS}
          value={PAGE_SIZE_OPTIONS.filter(
            (option) => option.id === String(limit),
          )}
          local
          fontSize="12px"
          minWidth="120px"
          handleSelect={handleRowsPerPageChange}
        />
      </Box>
    </Box>
  );

  const FilterFields = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box sx={filterSearchItemSx}>
          <OutlinedInput
            value={store.searchDraft}
            onChange={(e) => store.setSearchDraft(e.target.value)}
            placeholder="Search invoice no / order no..."
            size="small"
            fullWidth
            startAdornment={
              <InputAdornment position="start">
                <Search
                  sx={{ fontSize: 18, color: swayyColors.textSecondary }}
                />
              </InputAdornment>
            }
            endAdornment={
              store.searchDraft ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <Clear sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined
            }
            sx={searchInputSx}
          />
        </Box>
        <Box sx={{ ...filterItemSx, ...filterDropdownWrapSx }}>
          <AppSearchDropdownMulti
            title="Select Status"
            isMultiSelect={false}
            list={SWAYY_INVOICE_STATUS_OPTIONS}
            value={SWAYY_INVOICE_STATUS_OPTIONS.filter((option) =>
              store.draftStatus.includes(option.id),
            )}
            local
            fontSize="12px"
            inputHeight={`${FILTER_FIELD_HEIGHT}px`}
            borderRadius={swayyRadii.sm}
            borderColor={swayyColors.border}
            handleSelect={(items) =>
              store.setDraftStatus(items.map((item) => item.id))
            }
          />
        </Box>
        <Box sx={{ ...filterItemSx, ...filterDropdownWrapSx }}>
          <AppSearchDropdownMulti
            title="Select Type"
            isMultiSelect={false}
            list={TYPE_OPTIONS}
            value={TYPE_OPTIONS.filter((option) =>
              store.draftType.includes(option.id),
            )}
            local
            fontSize="12px"
            inputHeight={`${FILTER_FIELD_HEIGHT}px`}
            borderRadius={swayyRadii.sm}
            borderColor={swayyColors.border}
            handleSelect={(items) =>
              store.setDraftType(items.map((item) => item.id))
            }
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          justifyContent: { xs: "stretch", md: "flex-end" },
          flexWrap: "wrap",
        }}
      >
        <AppButton
          label="Clear filters"
          onClick={handleClearFilters}
          customStyles={{
            minWidth: { xs: "100%", sm: 140 },
            height: 40,
            minHeight: 40,
            borderRadius: swayyRadii.md,
            bgcolor: "transparent",
            color: swayyColors.deepGreen,
            border: `1px solid ${swayyColors.border}`,
            "&:hover": {
              bgcolor: swayyColors.pineLight,
              border: `1px solid ${swayyColors.pine}`,
            },
          }}
        />
        <AppButton
          label="Apply"
          onClick={handleApplyFilters}
          customStyles={{
            minWidth: { xs: "100%", sm: 140 },
            height: 40,
            minHeight: 40,
            borderRadius: swayyRadii.md,
            bgcolor: swayyColors.pine,
            color: swayyColors.white,
            "&:hover": { bgcolor: swayyColors.pineHover },
          }}
        />
      </Box>
    </Box>
  );

  if (
    !userStore.hasSwayyPermission([
      SwayyEmployeeRoles.VIEW_INVOICES,
      SwayyEmployeeRoles.EDIT_INVOICES,
    ])
  ) {
    return <PermissionBlockLabel />;
  }

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        bgcolor: swayyColors.pageBg,
        minHeight: "100%",
      }}
    >
      <SwayyPageHeader
        title="Invoices"
        description="Track invoice generation, download PDFs, and regenerate failed or issued documents."
      />

      <Box sx={{ display: { xs: "block", md: "none" }, mb: 2 }}>
        <AppButton
          label="Filters"
          startIcon={<FilterList />}
          onClick={handleOpenMobileFilters}
          customStyles={{
            width: "100%",
            height: 40,
            minHeight: 40,
            borderRadius: swayyRadii.md,
            bgcolor: swayyColors.white,
            color: swayyColors.deepGreen,
            border: `1px solid ${swayyColors.border}`,
            "&:hover": { bgcolor: swayyColors.pineLight },
          }}
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          display: { xs: "none", md: "block" },
          mb: 2,
          borderRadius: swayyRadii.lg,
          border: `1px solid ${swayyColors.border}`,
          bgcolor: swayyColors.white,
          overflow: "visible",
        }}
      >
        <Box
          onClick={store.toggleFiltersOpen}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1.25,
            cursor: "pointer",
            borderTopLeftRadius: swayyRadii.lg,
            borderTopRightRadius: swayyRadii.lg,
            borderBottomLeftRadius: store.filtersOpen ? 0 : swayyRadii.lg,
            borderBottomRightRadius: store.filtersOpen ? 0 : swayyRadii.lg,
            borderBottom: store.filtersOpen
              ? `1px solid ${swayyColors.border}`
              : "none",
            "&:hover": { backgroundColor: swayyColors.pineLight },
          }}
        >
          <CustomTypography
            variant="heading6"
            color={swayyColors.deepGreen}
            sx={{ fontFamily: swayyFont, fontWeight: 600 }}
          >
            Search & Filters
          </CustomTypography>
          <IconButton
            size="small"
            sx={{ color: swayyColors.pine }}
            onClick={(e) => {
              e.stopPropagation();
              store.toggleFiltersOpen();
            }}
          >
            {store.filtersOpen ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={store.filtersOpen} timeout="auto" unmountOnExit>
          <Box sx={{ p: 2 }}>{FilterFields}</Box>
        </Collapse>
      </Paper>

      <Drawer
        anchor="right"
        open={mobileFiltersOpen}
        onClose={handleCloseMobileFilters}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 360 },
            p: 2,
            bgcolor: swayyColors.white,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <CustomTypography
            variant="heading6"
            color={swayyColors.deepGreen}
            sx={{ fontFamily: swayyFont, fontWeight: 600 }}
          >
            Filters
          </CustomTypography>
          <IconButton
            size="small"
            onClick={handleCloseMobileFilters}
            sx={{ color: swayyColors.deepGreen }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
        {FilterFields}
      </Drawer>

      <Paper
        elevation={0}
        sx={{
          borderRadius: swayyRadii.lg,
          border: `1px solid ${swayyColors.border}`,
          overflow: "hidden",
        }}
      >
        {!store.loading && store.error && store.invoices.length === 0 ? (
          <SwayyTableEmptyState
            title="Couldn't load invoices"
            description={store.error}
            actionLabel="Try again"
            onAction={() => {
              void handleLoadInvoices();
            }}
            danger
          />
        ) : (
          <>
        <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={thLeftSx}>Invoice</TableCell>
                <TableCell sx={thSx}>Order</TableCell>
                <TableCell sx={thSx}>Type</TableCell>
                <TableCell sx={thSx}>Status</TableCell>
                <TableCell sx={thSx}>Amount</TableCell>
                <TableCell sx={thSx}>Issued</TableCell>
                <TableCell sx={stickyActionsThSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {store.loading
                ? Array.from({ length: 6 }).map((_, index) => (
                    <TableRow key={index}>
                      {Array.from({ length: 7 }).map((__, cell) => (
                        <TableCell
                          key={cell}
                          sx={cell === 0 ? tdLeftSx : tdSx}
                        >
                          <Skeleton height={22} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : store.invoices.length === 0
                  ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 6, textAlign: "center" }}>
                          <CustomTypography
                            variant="body2"
                            color={
                              store.error
                                ? swayyColors.danger
                                : swayyColors.textSecondary
                            }
                            sx={{ fontFamily: swayyFont }}
                          >
                            {store.error || "No invoices found."}
                          </CustomTypography>
                        </TableCell>
                      </TableRow>
                    )
                  : store.invoices.map((invoice) => (
                      <TableRow
                        key={invoice.id}
                        hover
                        sx={{
                          "&:hover": { bgcolor: swayyColors.pineLight },
                          "&:hover .sticky-actions-cell": {
                            bgcolor: swayyColors.pineLight,
                          },
                        }}
                      >
                        <TableCell sx={tdLeftSx}>
                          <Box sx={{ maxWidth: "100%" }}>
                            <SkuCopyRow
                              sku={invoice.invoice_number}
                              emptyFallback="—"
                              copyLabel="Copy invoice id"
                              color={swayyColors.pine}
                              fontWeight={600}
                              typographySx={{
                                fontFamily: swayyFont,
                                fontSize: 13,
                                textDecoration: "underline",
                                textUnderlineOffset: "3px",
                                "&:hover": { color: swayyColors.pineHover },
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={tdSx}>
                          {invoice.order_id ? (
                            <Box
                              onClick={() =>
                                handleOpenOrder(invoice.order_id!)
                              }
                              sx={{ cursor: "pointer", maxWidth: "100%" }}
                            >
                              <SkuCopyRow
                                sku={
                                  invoice.order_number ||
                                  `#${invoice.order_id}`
                                }
                                emptyFallback="—"
                                copyLabel="Copy order"
                                color={swayyColors.pine}
                                fontWeight={600}
                                typographySx={{
                                  fontFamily: swayyFont,
                                  fontSize: 13,
                                  textDecoration: "underline",
                                  textUnderlineOffset: "3px",
                                  "&:hover": { color: swayyColors.pineHover },
                                }}
                              />
                            </Box>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tdSx,
                            textTransform: "capitalize",
                          }}
                        >
                          {String(invoice.type || "invoice").replace(/_/g, " ")}
                        </TableCell>
                        <TableCell sx={tdSx}>
                          <StatusBadge status={String(invoice.status || "")} />
                        </TableCell>
                        <TableCell
                          sx={{ ...tdSx, fontWeight: 600, color: swayyColors.deepGreen }}
                        >
                          {CurrencyHelper.formatCurrency(
                            invoice.grand_total ?? 0,
                            "INR",
                          )}
                        </TableCell>
                        <TableCell sx={tdSx}>
                          {invoice.issued_at
                            ? formatDate(invoice.issued_at)
                            : "—"}
                        </TableCell>
                        <TableCell
                          className="sticky-actions-cell"
                          sx={stickyActionsTdSx}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.75,
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {String(invoice.status || "").toLowerCase() ===
                              "pending" && (
                              <CustomTypography
                                variant="body3"
                                color={swayyColors.textSecondary}
                                sx={{
                                  fontFamily: swayyFont,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  fontStyle: "italic",
                                }}
                              >
                                Generating…
                              </CustomTypography>
                            )}
                            {String(invoice.status || "").toLowerCase() ===
                              "generated" &&
                              invoice.download_url && (
                              <IconButton
                                size="small"
                                sx={actionBtnSx}
                                onClick={() =>
                                  handleDownloadInvoice(invoice.download_url!)
                                }
                              >
                                <DownloadOutlined fontSize="small" />
                              </IconButton>
                            )}
                            {canEditInvoices &&
                              (String(invoice.status || "").toLowerCase() ===
                                "failed" ||
                                String(invoice.status || "").toLowerCase() ===
                                  "generated") && (
                              <IconButton
                                size="small"
                                sx={actionBtnSx}
                                disabled={store.actionLoadingId === invoice.id}
                                onClick={() => {
                                  void handleRegenerateInvoice(invoice.id);
                                }}
                              >
                                <ReplayOutlined fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
            </TableBody>
          </Table>
        </TableContainer>

        {store.invoices.length > 0 && paginationFooter}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default observer(SwayyInvoices);
